import { useMemo } from "react";

import type { ApiKeyStatusFilter } from "@/api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { cn } from "@/lib/utils";

interface FilterOption {
  value: ApiKeyStatusFilter;
  label: string;
}

// Filter segments map to translation keys so the labels re-render
// on a locale switch.
const FILTER_KEY: Record<ApiKeyStatusFilter, TranslationKey> = {
  all: "apiKeys.filter.all",
  active: "apiKeys.filter.active",
  disabled: "apiKeys.filter.disabled",
  revoked: "apiKeys.filter.revoked",
  expired: "apiKeys.filter.expired",
};

interface ApiKeysFilterBarProps {
  value: ApiKeyStatusFilter;
  onChange: (value: ApiKeyStatusFilter) => void;
}

/**
 * Segmented filter for the API keys list. Renders as a horizontal
 * scroll on mobile so the five segments don't overflow the
 * viewport.
 */
export function ApiKeysFilterBar({ value, onChange }: ApiKeysFilterBarProps) {
  const { t } = useTranslation();

  const options = useMemo<ReadonlyArray<FilterOption>>(
    () =>
      (["all", "active", "disabled", "revoked", "expired"] as const).map(
        (v) => ({ value: v, label: t(FILTER_KEY[v]) })
      ),
    [t]
  );

  return (
    <div className="flex gap-1 overflow-x-auto rounded-md border border-ocean-100 bg-ocean-50/30 p-1">
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
            className={cn(
              "shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
              isActive
                ? "bg-white text-ocean-900 shadow-sm"
                : "text-neutral-600 hover:bg-white/60 hover:text-ocean-800"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
